import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATIONS_API } from 'api';
import { BlockHeading } from 'components/BlockHeading';
import { ConfigurationBlockList } from 'components/ConfigurationBlock';
import { FormSearchField, FormNumEntriesSelector } from 'components/Form';
import { PaginationBlock } from 'components/PaginationBlock';
import { chunkArray, sortReferenceByAlphaNumeric } from 'utils';
import { AuthContext } from 'context/auth';
import { UiContext } from 'context/ui';
import {
	CONFIGURATION_STATUS,
	CONFIGURATION_TYPE,
	PROMISE_STATES,
	USER_ROLES,
} from 'utils/constant';
import ToastifyHandler from 'utils/ToastifyHandler';
import { useParams } from 'react-router-dom';
import { Spinner } from 'components/Spinner';

const SORT_TYPES = {
	lastEdited: 'lastEdited',
	created: 'created',
	reference: 'reference',
	user: 'user',
};

const DashboardBlock = ({
	type,
	typeStatus,
	titleLabel,
	configurationData,
	setConfigurationData,
	isAdmin = false,
}) => {
	const { t } = useTranslation('baseFormPlaceholders');
	const { hideAllConfigurations } = CONFIGURATIONS_API;
	const { currentUserAdditionalData } = useContext(AuthContext);
	const { showPopupByKey } = useContext(UiContext);
	const { companyId } = useParams();

	// Core states for sorting, filtering, and pagination
	const [sortedConfigurationData, setSortedConfigurationData] = useState(null); // Holds the full sorted and filtered dataset
	const [chunkedConfigurationData, setChunkedConfigurationData] = useState(null); // Page-chunked dataset (sorted and filtered)
	const [activeSortType, setActiveSortType] = useState(SORT_TYPES.lastEdited); // Default sort by lastEdited
	const [activePaginationPage, setActivePaginationPage] = useState(1); // Current page
	const [configurationCountPerPage, setConfigurationCountPerPage] = useState(5); // Items per page
	const [searchTerm, setSearchTerm] = useState(null); // Search input value
	const [isPaginationShown, setPaginationShown] = useState(false); // Whether to show pagination controls
	const [totalConfigurationItemsCount, setTotalConfigurationItemsCount] = useState(0); // Total items for pagination

	// Additional states for UI and actions
	const [loadingStatus, setLoadingStatus] = useState(PROMISE_STATES.default);
	const [selectedItems, setSelectedItems] = useState({});
	const [removeAllText, setRemoveAllText] = useState(
		typeStatus !== CONFIGURATION_STATUS.open
			? t('commonAppValues:deleteAll')
			: t('commonAppValues:completeAll')
	);
	const notificationsHandler = useRef(new ToastifyHandler());

	// Options for items per page dropdown
	const entriesPerPageOptions = [
		{ value: 3, label: '3' },
		{ value: 5, label: '5' },
		{ value: 10, label: '10' },
		{ value: 15, label: '15' },
	];

	// Utility function to sort data based on sort type
	const sortData = (data, sortType) => {
		const duplicatedData = structuredClone(data);
		if (sortType === SORT_TYPES.created || sortType === SORT_TYPES.lastEdited) {
			const ts = o => (typeof o === 'object' ? o.seconds * 1000 + o.nanoseconds / 1_000_000 : o);
			const getDate = item =>
				sortType === SORT_TYPES.created ? ts(item.publishedAt) : item.lastEditedDateInMilliseconds;
			duplicatedData.sort((a, b) => getDate(b) - getDate(a)); // Descending order
		} else if (sortType === SORT_TYPES.reference) {
			return sortReferenceByAlphaNumeric(duplicatedData); // Assumes this returns sorted array
		}
		return duplicatedData;
	};

	// Effect to compute sorted (and filtered) data
	useEffect(() => {
		if (!configurationData) return;

		let data = configurationData;

		// Apply search filter if present
		if (searchTerm !== null) {
			data = data.filter(({ reference, buildingReference, created, lastEdited }) => {
				const referenceLowerCase = reference.toLowerCase();
				const buildingReferenceLowerCase = buildingReference.toLowerCase();
				const createdLowerCase = created.toLowerCase();
				const lastEditedLowerCase = lastEdited.toLowerCase();
				return (
					referenceLowerCase.includes(searchTerm) ||
					buildingReferenceLowerCase.includes(searchTerm) ||
					createdLowerCase.includes(searchTerm) ||
					lastEditedLowerCase.includes(searchTerm)
				);
			});
		}

		// Sort the filtered or full data
		const sortedData = sortData(data, activeSortType);
		setSortedConfigurationData(sortedData);
	}, [configurationData, searchTerm, activeSortType]);

	// Effect to chunk sorted data into pages
	useEffect(() => {
		if (!sortedConfigurationData) return;

		const chunkedData = chunkArray(sortedConfigurationData, configurationCountPerPage);
		setChunkedConfigurationData(chunkedData);
		setTotalConfigurationItemsCount(sortedConfigurationData.length);
		setPaginationShown(chunkedData.length > 1);

		// Adjust active page if it exceeds the number of pages - no need to track this dep.
		if (activePaginationPage > chunkedData.length) {
			setActivePaginationPage(chunkedData.length || 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortedConfigurationData, configurationCountPerPage]);

	// Update removeAllText based on selected items
	useEffect(() => {
		const numSelected = Object.keys(selectedItems).length;
		if (typeStatus !== CONFIGURATION_STATUS.open) {
			setRemoveAllText(
				numSelected > 0
					? t('commonAppValues:deleteSomeFmt').replace('{}', numSelected)
					: t('commonAppValues:deleteAll')
			);
		} else {
			setRemoveAllText(
				numSelected > 0
					? t('commonAppValues:completeSomeFmt').replace('{}', numSelected)
					: t('commonAppValues:completeAll')
			);
		}
	}, [t, typeStatus, selectedItems]);

	// Handle sort type change
	const handleClickOnSort = (e, criteria) => {
		e.stopPropagation();
		setActiveSortType(criteria);
	};

	// Handle search input change
	const handleOnChangeSearchInput = e => {
		setSearchTerm(e.target.value.trim().toLowerCase() || null);
		setActivePaginationPage(1); // Reset to first page on search
	};

	// Handle page change
	const handlePaginationPageChange = pageNumber => {
		setActivePaginationPage(pageNumber);
	};

	// Handle item selection
	const handleSelected = (config, selected) => {
		const sel = { ...selectedItems };
		if (selected) {
			sel[config.code] = true;
		} else {
			delete sel[config.code];
		}
		setSelectedItems(sel);
	};

	// Remove or complete items
	const doRemoveAll = async (action, confType, itemIds = null) => {
		try {
			setLoadingStatus(PROMISE_STATES.pending);
			notificationsHandler.current.pending(t('notifications:pleaseWait'));
			const asAdmin = isAdmin || currentUserAdditionalData.role === USER_ROLES.admin;
			const cid = asAdmin ? companyId || null : currentUserAdditionalData.companyId;
			await hideAllConfigurations(action, confType, typeStatus, cid, asAdmin, itemIds);
			setLoadingStatus(PROMISE_STATES.fulfilled);
			notificationsHandler.current.success(t('notifications:dataHasBeenSuccessfullyUpdated'));

			if (itemIds?.length > 0) {
				setConfigurationData(configurationData.filter(o => !selectedItems[o.code]));
				setSelectedItems({});
			} else {
				setConfigurationData([]);
			}
		} catch (error) {
			const { code } = error;
			notificationsHandler.current.rejected(t(code, { ns: 'firebaseErrorMessages' }));
			setLoadingStatus(PROMISE_STATES.rejected);
		}
	};

	// Trigger remove/complete action
	const handleRemoveAll = e => {
		e.preventDefault();
		e.stopPropagation();
		let confType;
		if (type === 'orders') confType = CONFIGURATION_TYPE.order;
		else if (type === 'requests') confType = CONFIGURATION_TYPE.request;
		else if (type === 'drafts') confType = CONFIGURATION_TYPE.drafts;
		const action = typeStatus === CONFIGURATION_STATUS.open ? 'complete' : 'delete';
		const itemIds = Object.keys(selectedItems);
		const selectAll = itemIds.length === 0;
		const partial = selectAll ? 'All' : 'Some';
		const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);
		const title = t(
			`commonAppValues:confirm${capitalizeFirst(action)}${partial}${capitalizeFirst(type)}${selectAll ? '' : 'Fmt'}`
		).replace('{}', itemIds.length);
		const message =
			isAdmin && action === 'delete' ? t('commonAppValues:confirmDeleteAdminNote') : '';
		showPopupByKey('genericWarning', {
			title,
			message,
			confirmDialog: isConfirmed =>
				isConfirmed && doRemoveAll(action, confType, selectAll ? null : itemIds),
		});
	};

	return (
		<div className="dashboard_block">
			<div className="dashboard_block__heading">
				<BlockHeading title={t(titleLabel)} />
				<div className="dashboard_block__header_container">
					{type === 'drafts' && (
						<div className="dashboard_block__field">
							<button
								className="btn_v2"
								type="button"
								disabled={loadingStatus === PROMISE_STATES.pending}
								onClick={handleRemoveAll}
							>
								{removeAllText}
							</button>
						</div>
					)}
					{type === 'requests' && (
						<div className="dashboard_block__field">
							<button
								className="btn_v2"
								type="button"
								disabled={loadingStatus === PROMISE_STATES.pending}
								onClick={handleRemoveAll}
							>
								{removeAllText}
							</button>
						</div>
					)}
					{type === 'orders' && (
						<div className="dashboard_block__field">
							<button
								className="btn_v2"
								type="button"
								disabled={loadingStatus === PROMISE_STATES.pending}
								onClick={handleRemoveAll}
							>
								{removeAllText}
							</button>
						</div>
					)}
					<div className="dashboard_block__field">
						<FormNumEntriesSelector
							id={`${titleLabel}-NumEntries`}
							defaultValue={entriesPerPageOptions.find(o => o.value === configurationCountPerPage)}
							options={entriesPerPageOptions}
							onChange={selectedOption => {
								setConfigurationCountPerPage(selectedOption.value);
							}}
						/>
					</div>
					<div className="dashboard_block__field">
						<FormSearchField
							id={titleLabel}
							placeholder={t('searchField')}
							name={titleLabel}
							onChange={handleOnChangeSearchInput}
						/>
					</div>
				</div>
			</div>
			{loadingStatus === PROMISE_STATES.pending ? (
				<Spinner darkTheme />
			) : (
				<>
					{chunkedConfigurationData && chunkedConfigurationData.length > 0 ? (
						<ConfigurationBlockList
							configurationData={chunkedConfigurationData[activePaginationPage - 1]}
							selectedItems={selectedItems}
							handleClickOnSort={handleClickOnSort}
							handleSelected={handleSelected}
						/>
					) : null}
					{isPaginationShown ? (
						<PaginationBlock
							activePaginationPage={activePaginationPage}
							itemsCountPerPage={configurationCountPerPage}
							totalItemsCount={totalConfigurationItemsCount}
							handlePaginationPageChange={handlePaginationPageChange}
						/>
					) : null}
				</>
			)}
		</div>
	);
};

export default DashboardBlock;
